import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  LayoutDashboard, Building2, FolderLock, Gauge, BarChart3, Bell, Settings,
  Target, Sparkles, Crown, Lock, ArrowRight, ChevronRight, Check,
} from 'lucide-react';

/* ── Hero visual, concept B: the product itself ────────────────────────────
   Instead of a metaphor (the founder/advisor "connect scene"), this panel is
   the signed-in Dashboard's consulting view drawn at hero scale: the same
   shell wash behind it, the same white cards on slate hairlines, the same
   three score rings (goal group in yellow, IR score in blue, Capital
   Readiness as the premium gradient card with its ribbon and crown), the same
   profile-completion bar, and the advisor row underneath. A visitor sees what
   they actually get the moment they sign in.

   Colours are copied from AppShell/Dashboard rather than imported: those
   tokens are brand-switched per domain (yellow on connectcorporate.com) and
   sit behind the app bundle, while this panel must always read
   consulting-blue. If the app's palette moves, mirror it here.

   Numbers are illustrative — one plausible founder mid-journey, not a claim.
──────────────────────────────────────────────────────────────────────────── */

/* dashboard palette */
const SHELL = 'linear-gradient(180deg,#E9F1FE 0%,#F1F5FB 30%,#EFF4FB 100%)';
const ACCENT = '#1976D2';
const ACCENT_SOFT = '#EFF6FF';
const ACCENT_ON_SOFT = '#1E4E8C';
const INK = '#111827';
const MUTED = '#6B7280';
const FAINT = '#9CA3AF';
const LINE = '#E5E7EB';
const HAIR = '#EDF1F7';
const TRACK = '#F3F4F6';
const YELLOW = '#FACC15';
const GREEN = '#15824E';

const SHADOW = '0 34px 70px -30px rgba(23,38,58,.38)';

const AV_FOUNDER = 'https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/l4.png';
const AV_ADVISOR = 'https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/l3.png';

/* ── motion helpers: rings draw and counters run once, when first seen ──── */
function useInView<T extends HTMLElement>(threshold = 0.25) {
  const ref = useRef<T>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(es => {
      if (es[0]?.isIntersecting) { setSeen(true); obs.disconnect(); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, seen] as const;
}

function useCount(target: number, run: boolean, decimals = 0) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!run) return;
    let raf = 0;
    let start = 0;
    const tick = (now: number) => {
      if (!start) start = now;
      const p = Math.min(1, (now - start) / 1150);
      setVal(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, run]);
  return val.toFixed(decimals);
}

/* ── pieces ─────────────────────────────────────────────────────────────── */

/** The Dashboard's gauge, scaled: the app draws r=70 with a 12 stroke. */
function Ring({ pct, run, size = 68, stroke, track = TRACK, delay = 0, glow, children }: {
  pct: number; run: boolean; size?: number; stroke: string; track?: string;
  delay?: number; glow?: boolean; children: ReactNode;
}) {
  const r = 42;
  const c = 2 * Math.PI * r;
  return (
    <div style={{ position: 'relative', width: size, height: size, flex: 'none' }}>
      <svg viewBox="0 0 100 100" width={size} height={size} style={{ transform: 'rotate(-90deg)', display: 'block' }}>
        <defs>
          <linearGradient id="hdCrRing" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#4CA1F0" /><stop offset="0.5" stopColor="#1976D2" /><stop offset="1" stopColor="#125AA6" />
          </linearGradient>
        </defs>
        <circle cx="50" cy="50" r={r} fill="none" stroke={track} strokeWidth="7.2" />
        <circle
          cx="50" cy="50" r={r} fill="none" stroke={stroke} strokeWidth="7.2" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={run ? c - (c * pct) / 100 : c}
          style={{
            transition: `stroke-dashoffset 1.2s cubic-bezier(.2,.7,.2,1) ${delay}ms`,
            filter: glow ? 'drop-shadow(0 0 6px rgba(25,118,210,.45))' : undefined,
          }}
        />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
        {children}
      </div>
    </div>
  );
}

/** Card head: the app's small icon tile plus its bold title. */
function CardHead({ icon, tint, title, ink = INK, mt = 0, mb = 9 }: { icon: ReactNode; tint: string; title: string; ink?: string; mt?: number; mb?: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: mt, marginBottom: mb }}>
      <span style={{ width: 15, height: 15, borderRadius: 5, background: tint, display: 'grid', placeItems: 'center', flex: 'none' }}>{icon}</span>
      <b style={{ fontSize: 9.5, fontWeight: 800, color: ink, letterSpacing: '-0.01em' }}>{title}</b>
    </div>
  );
}

const cardBase: React.CSSProperties = {
  background: '#fff', border: `1px solid ${HAIR}`, borderRadius: 13,
  boxShadow: '0 1px 2px rgba(16,24,40,.05)', padding: '11px 9px',
  display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
};

const ghostPill: React.CSSProperties = {
  marginTop: 'auto', display: 'inline-flex', alignItems: 'center', gap: 3,
  padding: '4px 9px', borderRadius: 8, border: `1px solid ${LINE}`,
  background: '#fff', color: '#374151', fontSize: 7.5, fontWeight: 800,
  whiteSpace: 'nowrap',
};

/* ── panel ──────────────────────────────────────────────────────────────── */
export default function HeroDashboard() {
  const [ref, seen] = useInView<HTMLDivElement>();
  const ir = useCount(72, seen);
  const cr = useCount(72.5, seen, 1);
  const profile = useCount(100, seen);

  const rail = [LayoutDashboard, Building2, FolderLock, Gauge, BarChart3, Bell];

  return (
    <div ref={ref} style={{ position: 'relative', zIndex: 2 }}>
      <style>{`
        @keyframes hd-pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.35;transform:scale(.8)}}
        .hd-live{animation:hd-pulse 1.9s ease-in-out infinite}
        .hd-bar{transition:width 1.3s cubic-bezier(.2,.7,.2,1) .15s}
      `}</style>

      <div style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 20, boxShadow: SHADOW, overflow: 'hidden' }}>
        {/* browser chrome — what this shows is a screen in the product, not a graphic */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: 30, padding: '0 11px', background: '#F7F9FC', borderBottom: `1px solid ${HAIR}` }}>
          {[0, 1, 2].map(i => (
            <span key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: '#E2E6EC' }} />
          ))}
          <span style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fff', border: `1px solid ${HAIR}`, borderRadius: 999, padding: '2px 9px', fontSize: 7.5, fontWeight: 700, color: FAINT }}>
              <Lock size={7} strokeWidth={2.4} /> conncct.com/dashboard
            </span>
          </span>
          <span style={{ width: 30 }} />
        </div>

        <div style={{ display: 'flex', minHeight: 372 }}>
          {/* mini sidebar rail */}
          <div style={{ width: 40, flex: 'none', background: '#fff', borderRight: `1px solid ${HAIR}`, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 9 }}>
            <span style={{ height: 34, display: 'grid', placeItems: 'center' }}>
              <span style={{ width: 20, height: 20, borderRadius: 6, background: '#17263A', display: 'grid', placeItems: 'center' }}>
                <svg viewBox="0 0 44 16" width="13" height="6" fill="none">
                  <line x1="11" y1="8" x2="33" y2="8" stroke="#7fc0ff" strokeWidth="3" />
                  <circle cx="11" cy="8" r="5" fill="#1976D2" stroke="#7fc0ff" strokeWidth="3" />
                  <circle cx="33" cy="8" r="5" fill="#fff" stroke="#7fc0ff" strokeWidth="3" />
                </svg>
              </span>
            </span>
            <div style={{ width: 22, height: 1, background: HAIR, margin: '0 0 8px' }} />
            {rail.map((Icon, i) => {
              const active = i === 0;
              return (
                <span key={i} style={{ position: 'relative', width: 26, height: 26, borderRadius: 7, display: 'grid', placeItems: 'center', marginBottom: 4, background: active ? ACCENT_SOFT : 'transparent' }}>
                  <Icon size={12} strokeWidth={active ? 2.3 : 1.8} color={active ? ACCENT : '#B6BDC9'} />
                  {Icon === Bell && <span style={{ position: 'absolute', top: 4, right: 4, width: 4, height: 4, borderRadius: '50%', background: '#EF4444' }} />}
                </span>
              );
            })}
            <span style={{ marginTop: 'auto', width: 26, height: 26, borderRadius: 7, display: 'grid', placeItems: 'center' }}>
              <Settings size={12} strokeWidth={1.8} color="#B6BDC9" />
            </span>
          </div>

          {/* main column, on the app's own wash */}
          <div style={{ flex: 1, background: SHELL, padding: '11px 12px 13px', minWidth: 0 }}>
            {/* header row */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: INK, letterSpacing: '-0.02em' }}>Dashboard</div>
                <div style={{ fontSize: 8, fontWeight: 600, color: MUTED, marginTop: 2 }}>Welcome back, Omar. Here's what's happening today.</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 'none' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: '#fff', border: '1.5px solid #3B82F6', borderRadius: 8, padding: '3px 6px 3px 8px', fontSize: 7.5, fontWeight: 800, color: '#374151' }}>
                  Consulting <ChevronRight size={8} strokeWidth={2.6} color={FAINT} />
                </span>
                <img src={AV_FOUNDER} alt="" loading="lazy" style={{ width: 21, height: 21, borderRadius: '50%', objectFit: 'cover', border: '1.5px solid #fff', boxShadow: '0 2px 6px rgba(16,24,40,.16)' }} />
              </div>
            </div>

            {/* the three score cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 7 }}>
              {/* goal assessment */}
              <div style={cardBase}>
                <CardHead icon={<Target size={8.5} strokeWidth={2.4} color={ACCENT_ON_SOFT} />} tint={ACCENT_SOFT} title="Goal" />
                <Ring pct={62} run={seen} stroke={YELLOW} delay={40}>
                  <b style={{ fontSize: 17, fontWeight: 800, color: INK, letterSpacing: '-0.03em' }}>G4</b>
                </Ring>
                <div style={{ fontSize: 7, fontWeight: 700, color: MUTED, lineHeight: 1.4, margin: '8px 0 9px', minHeight: 20 }}>
                  Growth capital<br />Series A track
                </div>
                <span style={ghostPill}>View more</span>
              </div>

              {/* IR score */}
              <div style={cardBase}>
                <CardHead icon={<BarChart3 size={8.5} strokeWidth={2.4} color={ACCENT} />} tint="#EAF2FB" title="IR Score" />
                <Ring pct={72} run={seen} stroke={ACCENT} delay={160}>
                  <b style={{ fontSize: 17, fontWeight: 800, color: ACCENT, letterSpacing: '-0.03em' }}>{ir}</b>
                  <small style={{ fontSize: 6, fontWeight: 800, color: FAINT, marginTop: 2 }}>/ 100</small>
                </Ring>
                <div style={{ margin: '8px 0 9px', minHeight: 20 }}>
                  <span style={{ display: 'inline-block', padding: '2px 7px', borderRadius: 999, background: 'rgba(25,118,210,.1)', color: ACCENT, fontSize: 7, fontWeight: 800 }}>Investor-Ready</span>
                </div>
                <span style={ghostPill}>View report <ArrowRight size={7} strokeWidth={2.8} /></span>
              </div>

              {/* capital readiness — the premium card, ribbon and crown included */}
              <div style={{
                ...cardBase,
                position: 'relative', overflow: 'hidden',
                border: '1px solid #C7DCF5',
                background: 'linear-gradient(165deg,#EAF2FB 0%,#FFFFFF 58%)',
                boxShadow: '0 16px 32px -18px rgba(25,118,210,.55)',
              }}>
                <span style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg,#2E83E6,#1976D2,#17263A)' }} />
                <span style={{ position: 'absolute', top: 5, right: 5, display: 'inline-flex', alignItems: 'center', gap: 2, padding: '1px 5px', borderRadius: 999, background: 'linear-gradient(120deg,#17263A,#1E3A5F)', color: '#FFD54F', fontSize: 5.5, fontWeight: 900, letterSpacing: '.06em', textTransform: 'uppercase' }}>
                  <Crown size={5.5} strokeWidth={2.6} /> Premium
                </span>
                <CardHead icon={<Sparkles size={8.5} strokeWidth={2.4} color={ACCENT} />} tint="#DCEBFB" title="Capital" ink="#17263A" mt={7} mb={2} />
                <Ring pct={72.5} run={seen} stroke="url(#hdCrRing)" track="#E4ECF6" delay={280} glow>
                  <b style={{ fontSize: 16, fontWeight: 900, color: '#17263A', letterSpacing: '-0.03em' }}>{cr}</b>
                  <small style={{ fontSize: 6, fontWeight: 800, color: '#94A3B8', marginTop: 2 }}>/ 100</small>
                </Ring>
                <div style={{ margin: '8px 0 9px', minHeight: 20 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, padding: '2px 7px', borderRadius: 999, background: '#EAF2FB', color: '#125AA6', fontSize: 7, fontWeight: 800 }}>
                    <Sparkles size={6} strokeWidth={2.6} /> R6 · Ready
                  </span>
                </div>
                <span style={{ ...ghostPill, border: 'none', color: '#fff', background: 'linear-gradient(120deg,#2E83E6,#1976D2 55%,#17263A)', boxShadow: '0 5px 12px -6px rgba(25,118,210,.8)' }}>
                  Breakdown <ArrowRight size={7} strokeWidth={2.8} />
                </span>
              </div>
            </div>

            {/* profile completion */}
            <div style={{ background: '#fff', border: `1px solid ${HAIR}`, borderRadius: 13, boxShadow: '0 1px 2px rgba(16,24,40,.05)', padding: '10px 11px', marginTop: 7 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                <div>
                  <b style={{ fontSize: 9.5, fontWeight: 800, color: INK }}>Profile Completion</b>
                  <div style={{ fontSize: 7, fontWeight: 600, color: MUTED, marginTop: 1.5 }}>Every step done, fully investor-ready</div>
                </div>
                <b style={{ fontSize: 15, fontWeight: 800, color: GREEN, letterSpacing: '-0.03em', flex: 'none' }}>{profile}%</b>
              </div>
              <div style={{ height: 5, borderRadius: 999, background: TRACK, overflow: 'hidden', margin: '7px 0 8px' }}>
                <div className="hd-bar" style={{ height: '100%', borderRadius: 999, width: seen ? '100%' : '0%', background: 'linear-gradient(90deg,#10B981,#15824E)' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 5 }}>
                {['Goal Assessment', 'IR Score', 'Capital Assessment'].map(s => (
                  <span key={s} style={{ display: 'flex', alignItems: 'center', gap: 4, background: ACCENT_SOFT, borderRadius: 8, padding: '4px 5px' }}>
                    <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#1D74F5', display: 'grid', placeItems: 'center', flex: 'none' }}>
                      <Check size={7} strokeWidth={3.4} color="#fff" />
                    </span>
                    <span style={{ fontSize: 6.5, fontWeight: 800, color: INK, lineHeight: 1.2 }}>{s}</span>
                  </span>
                ))}
              </div>
            </div>

            {/* your advisor — the human half of the page, in the app's own clothes */}
            <div style={{ background: '#fff', border: `1px solid ${HAIR}`, borderRadius: 13, boxShadow: '0 1px 2px rgba(16,24,40,.05)', padding: '10px 11px', marginTop: 7 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <img src={AV_ADVISOR} alt="" loading="lazy" style={{ width: 28, height: 28, borderRadius: 9, objectFit: 'cover', flex: 'none' }} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 6.5, fontWeight: 800, color: FAINT, letterSpacing: '.1em', textTransform: 'uppercase' }}>Your advisor</div>
                  <b style={{ fontSize: 9.5, fontWeight: 800, color: INK, display: 'block', marginTop: 1 }}>Sara N. · Ex-VC, Fractional CFO</b>
                </div>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '3px 7px', borderRadius: 999, background: ACCENT_SOFT, color: ACCENT_ON_SOFT, fontSize: 7, fontWeight: 800, flex: 'none' }}>
                  <span className="hd-live" style={{ width: 4, height: 4, borderRadius: '50%', background: '#10B981' }} /> Connected
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 8 }}>
                <span style={{ fontSize: 7, fontWeight: 800, color: MUTED, flex: 'none' }}>6-week plan</span>
                <span style={{ flex: 1, height: 4, borderRadius: 999, background: TRACK, overflow: 'hidden' }}>
                  <span className="hd-bar" style={{ display: 'block', height: '100%', borderRadius: 999, width: seen ? '62%' : '0%', background: `linear-gradient(90deg,#60A5FA,${ACCENT})` }} />
                </span>
                <span style={{ fontSize: 7, fontWeight: 800, color: ACCENT, flex: 'none' }}>5 / 9 done</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
